import { useEffect } from "react";

// Reveal effects are an enhancement: without JavaScript, content stays visible.
if (typeof document !== "undefined") {
    document.documentElement.classList.add("reveal-enabled");
}

export function useInView({ defaultOnce = true, ...observerOptions } = {}) {
    useEffect(() => {
        const elements = document.querySelectorAll("[data-animation]");
        if (!elements.length) return;

        if (typeof IntersectionObserver === "undefined") {
            elements.forEach((el) => el.classList.add("animate"));
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const el = entry.target;
                    let config = { once: defaultOnce };

                    const attr = el.getAttribute("data-animation");
                    if (attr && attr !== "") {
                        try {
                            config = { ...config, ...JSON.parse(attr) };
                        } catch {
                            // si no es JSON, usamos defaultOnce
                        }
                    }

                    const { once, delay = 0, class: animationClass = "animate" } = config;

                    if (entry.isIntersecting) {
                        if (delay) el.style.transitionDelay = `${delay}s`;
                        el.classList.add(animationClass);
                        if (once) observer.unobserve(el);
                    } else if (!once) {
                        el.classList.remove(animationClass);
                    }
                });
            },
            { root: null, rootMargin: "0px 0px 15% 0px", threshold: 0, ...observerOptions }
        );

        elements.forEach((el) => observer.observe(el));

        // Never leave critical content hidden if an observer implementation stalls.
        const fallback = window.setTimeout(() => {
            elements.forEach((el) => el.classList.add("animate"));
            observer.disconnect();
        }, 1500);

        return () => {
            window.clearTimeout(fallback);
            observer.disconnect();
        };
    }, [defaultOnce, observerOptions]);
}
